/**
 * SyncStatusBar — schmale, selbst-refreshende Leiste mit dem letzten Lauf jedes
 * AV-Flows (Quick-Task 260831-nnv).
 *
 * Zeigt fuer jeden der 5 AV-Flows (ingest, refresh, carrier-track, stuck-check,
 * abholbereit-mail) Label + Status + relative Zeit — Backoffice soll ohne
 * Server-Zugang sehen koennen ob die Sync-Flows zuverlaessig laufen.
 *
 * Anders als MailOutboxWarning: KEIN early-return bei leerem Zustand — die
 * Leiste soll immer sichtbar sein (auch waehrend des ersten Ladens oder wenn
 * der Abruf scheitert, dann mit neutralem Platzhalter statt zu verschwinden).
 * Polling alle 30s (nicht 60s wie MailOutboxWarning).
 */

import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { api } from '@libero/core/services/api';
import {
  formatRelativeZeit,
  formatLaeuftSeit,
  formatVollerZeitstempel,
} from '../lib/syncStatusZeit';

interface SyncStatusEintrag {
  flowId: string;
  running: boolean;
  lastStatus: string | null;
  lastStartedAt: string | null;
  lastCompletedAt: string | null;
  lastSuccessAt: string | null;
}

const FLOW_LABELS: Record<string, string> = {
  'auftragsverfolgung-ingest': 'Neue Aufträge',
  'auftragsverfolgung-refresh': 'Status-Refresh',
  'auftragsverfolgung-carrier-track': 'DHL/GLS-Status',
  'auftragsverfolgung-stuck-check': 'Stuck-Check',
  'auftragsverfolgung-abholbereit-mail': 'Abholbereit-Mail',
};

const POLL_INTERVAL_MS = 30_000;

function StatusPunktOderIcon({ eintrag }: { eintrag: SyncStatusEintrag }) {
  if (eintrag.running) {
    return <RefreshCw className="w-3 h-3 text-[#1A1A1A] animate-spin flex-shrink-0" />;
  }
  const farbe =
    eintrag.lastStatus === 'completed'
      ? 'bg-green-500'
      : eintrag.lastStatus === 'failed'
        ? 'bg-red-500'
        : 'bg-gray-300';
  return <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${farbe}`} />;
}

export function SyncStatusBar() {
  const [flows, setFlows] = useState<SyncStatusEintrag[] | null>(null);

  useEffect(() => {
    let abgebrochen = false;

    const laden = async () => {
      try {
        const res = await api.get('/programmes/auftragsverfolgung/sync-status');
        if (!abgebrochen) setFlows(res.data?.data?.flows ?? null);
      } catch {
        // Leiste bleibt sichtbar mit neutralem Platzhalter — kein Verschwinden bei Fehler.
      }
    };

    laden();
    const timer = setInterval(laden, POLL_INTERVAL_MS);
    return () => {
      abgebrochen = true;
      clearInterval(timer);
    };
  }, []);

  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded border border-[#DAD9D9] bg-white px-3 py-2 text-xs text-[#1A1A1A]"
      role="status"
    >
      {!flows && <span className="text-gray-400">Sync-Status wird geladen …</span>}
      {flows &&
        flows.map((eintrag) => {
          const label = FLOW_LABELS[eintrag.flowId] ?? eintrag.flowId;
          const zeitText = eintrag.running
            ? formatLaeuftSeit(eintrag.lastStartedAt)
            : formatRelativeZeit(eintrag.lastCompletedAt);
          const title = eintrag.running
            ? `Gestartet: ${formatVollerZeitstempel(eintrag.lastStartedAt)}`
            : eintrag.lastCompletedAt
              ? `Letzter Lauf: ${formatVollerZeitstempel(eintrag.lastCompletedAt)}`
              : 'Noch nie gelaufen';

          return (
            <div key={eintrag.flowId} className="flex items-center gap-1.5" title={title}>
              <StatusPunktOderIcon eintrag={eintrag} />
              <span className="font-medium">{label}</span>
              <span className="text-gray-500">{zeitText}</span>
              {!eintrag.running && eintrag.lastStatus === 'failed' && (
                <span className="text-red-600">
                  {' '}
                  · Fehler
                  {eintrag.lastSuccessAt && (
                    <> (zuletzt OK {formatRelativeZeit(eintrag.lastSuccessAt)})</>
                  )}
                </span>
              )}
            </div>
          );
        })}
    </div>
  );
}

export default SyncStatusBar;
